import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import PopOverContent from './components/PopoverContent';

interface CustomPopoverInterface {
  clickableElement: JSX.Element | string;
  popOverContent: JSX.Element | string;
  inBetweenSpace?: number;
  version?: string;
  onClose?: () => void;
}

/**
 * Pop-over component - opens a popup near a target element
 * Clickable elements parent component should have a defined width
 * @param properties
 * @returns
 */
function CustomPopover(properties: CustomPopoverInterface) {
  const { clickableElement, popOverContent, inBetweenSpace, version, onClose } = properties;

  const popoverReference = useRef<HTMLDivElement | null>(null);
  const buttonReference = useRef<HTMLDivElement | null>(null);
  const [openPopover, setOpenPopover] = useState<boolean>(false);

  /**
   * Function to open a popover near a clickable element
   */
  const handleOpenPopover = () => {
    if (!openPopover && buttonReference.current) {
      setOpenPopover(true);
    }
  };
  /**
   * Function to close the popover
   */
  const handleClosePopover = () => {
    setOpenPopover(false);
    if (onClose) {
      onClose();
    }
  };
  /**
   * Function to handle click events
   * @param event
   */
  const handleClickOutside = (event: MouseEvent) => {
    // Check if click was outside the popover
    if (popoverReference.current && !popoverReference.current.contains(event.target as Node)) {
      setOpenPopover(false);
    }
  };

  /**
   * Function to determine the position of the popover
   * Uses useLayoutEffect to prevent visual jitter
   */
  const handlePopOverPosition = () => {
    if (!popoverReference.current || !buttonReference.current) return;

    const buttonRect = buttonReference.current.getBoundingClientRect();
    const popoverRect = popoverReference.current.getBoundingClientRect();

    const gap = inBetweenSpace || 6;
    const viewportWidth = window.innerWidth;
    const padding = 10;

    const isRTL = document.documentElement.dir === 'rtl' || document.body.dir === 'rtl';

    // TOP
    popoverReference.current.style.top = `${buttonRect.bottom + gap}px`;

    // Default align with button
    let finalLeft = isRTL ? buttonRect.right - popoverRect.width : buttonRect.left;

    // ---------- RIGHT OVERFLOW CHECK ----------
    const rightOverflow = finalLeft + popoverRect.width > viewportWidth - padding;

    if (rightOverflow && !isRTL) {
      finalLeft = viewportWidth - popoverRect.width - padding;
    }

    // ---------- LEFT OVERFLOW CHECK ----------
    if (finalLeft < padding) {
      finalLeft = padding;
    }

    // In RTL, if it overflows the right edge, we might need to nudge it left
    if (isRTL && buttonRect.right > viewportWidth - padding) {
      // If the button itself is too far right, align with viewport right
      finalLeft = viewportWidth - popoverRect.width - padding;
    }

    popoverReference.current.style.left = `${finalLeft}px`;
  };

  useLayoutEffect(() => {
    if (!openPopover) return undefined;

    handlePopOverPosition();

    window.addEventListener('resize', handlePopOverPosition);
    window.addEventListener('scroll', handlePopOverPosition, true);

    const resizeObserver = new ResizeObserver(() => {
      handlePopOverPosition();
    });

    if (popoverReference.current) {
      resizeObserver.observe(popoverReference.current);
    }

    return () => {
      window.removeEventListener('resize', handlePopOverPosition);
      window.removeEventListener('scroll', handlePopOverPosition, true);
      resizeObserver.disconnect();
    };
  }, [openPopover]);

  // Add event listener when component mounts, remove on unmount
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Clone the popOverContent and inject the close function if it's a React element
  const enhancedPopOverContent = React.isValidElement(popOverContent)
    ? React.cloneElement(popOverContent as React.ReactElement<any>, {
        onClose: handleClosePopover,
      })
    : popOverContent;

  return (
    <div className="custom-popover-wrap">
      <div
        className="pop-over-element"
        role="presentation"
        ref={buttonReference}
        onClick={handleOpenPopover}>
        {clickableElement}
      </div>
      {openPopover ? (
        <PopOverContent
          openPopover={openPopover}
          popOverContent={enhancedPopOverContent}
          ref={popoverReference}
          version={version}
        />
      ) : null}
    </div>
  );
}

export default CustomPopover;
